import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeConfigurations } from '../../../utils/Configuration/FontAwesomeConfiguration';
import LeaderboardCardSimple from '../../components/LeaderboardCardSimple';
import useOutsideAlerter from '../../hooks/useOutsideAlerter';
import AccessibilityContainer from './containers/AccessibilityContainer';
import ProfileContainer from './containers/ProfileContainer';
import SettingsContainer from './containers/SettingsContainer';
import UpdatesContainer from './containers/UpdatesContainer';
import './style.scss';
import { Accessibility, TrappedFocus } from '../../../utils/Accessibility/Accessibility';

export interface ProfileDropdownModuleProps {
  onBlur;
  numUnreadNotis: number;
  numUnreadMessages: number;
}

const ProfileDropdownModule: React.FC<ProfileDropdownModuleProps> = ({ ...props }) => {
  const [collapsedStates, setCollapsedStates] = useState([true, true, true, true]);
  const [numUnreadNotis, setNumUnreadNotis] = useState(props.numUnreadNotis);
  const [numUnreadMessages, setNumUnreadMessages] = useState(props.numUnreadMessages);
  const wrapperRef = useRef(null);
  const ref = useRef(null);
  const [trappedChildren, setTrappedChildren] = useState<TrappedFocus>();

  useEffect(() => {
    const trappedChildren = Accessibility.trapFocus($('.profile_dropdown'));
    setTrappedChildren(trappedChildren);
  }, []);

  useOutsideAlerter(wrapperRef, e => {
    props.onBlur();
  });

  useEffect(() => {
    const eventMessageFunction = (event, message) => {
      if (message.user?.user_id == App.INSTANCE.CurrentUser.Id) {
        return;
      }
      if (App.INSTANCE.CurrentUser.isInConversation(message.channel)) {
        setNumUnreadMessages(numUnreadMessages + 1);
      }
    };
    PubNubCenter.INSTANCE?.addEventListener(PubNubCenter.ON_MESSAGE, eventMessageFunction, this);
    PubNubCenter.INSTANCE?.addEventListener(PubNubCenter.ON_REQUEST, eventMessageFunction, this);

    const eventSocialFunction = (event, message) => {
      if (message.user?.user_id == App.INSTANCE.CurrentUser.Id) {
        return;
      }
      if (App.INSTANCE.CurrentUser.isInConversation(message.channel)) {
        if (message.mark_read) {
          setNumUnreadNotis(current => {
            return current - 1;
          });
        } else {
          setNumUnreadNotis(current => {
            return current + 1;
          });
        }
      }
    };
    PubNubCenter.INSTANCE?.addEventListener(PubNubCenter.ON_SOCIAL_NOTIFICATION, eventSocialFunction, this);
    PubNubCenter.INSTANCE?.addEventListener(PubNubCenter.ON_SOCIAL_NOTIFICATION_READ, eventSocialFunction, this);

    const eventSetFunction = (event, message) => {
      if (App.INSTANCE.CurrentUser.isInConversation(message.channel)) {
        setNumUnreadNotis(message.count);
      }
    };
    PubNubCenter.INSTANCE?.addEventListener(PubNubCenter.ON_SET_NOTIFICATIONS_COUNT, eventSetFunction, this);

    return () => {
      PubNubCenter.INSTANCE?.removeEventListener(PubNubCenter.ON_MESSAGE, eventMessageFunction, this);
      PubNubCenter.INSTANCE?.removeEventListener(PubNubCenter.ON_REQUEST, eventMessageFunction, this);
      PubNubCenter.INSTANCE?.removeEventListener(PubNubCenter.ON_SOCIAL_NOTIFICATION, eventSocialFunction, this);
      PubNubCenter.INSTANCE?.removeEventListener(PubNubCenter.ON_SOCIAL_NOTIFICATION_READ, eventSocialFunction, this);
      PubNubCenter.INSTANCE?.removeEventListener(PubNubCenter.ON_SET_NOTIFICATIONS_COUNT, eventSetFunction, this);
      if (trappedChildren) {
        Accessibility.removeFromFocusStack(trappedChildren);
      }
    };
  }, []);

  return (
    <>
      <motion.div className={`profile_dropdown`} ref={wrapperRef}>
        <LeaderboardCardSimple onBlur={props.onBlur} />
        <div className="profile_dropdown_inner">
          <ProfileContainer
            collapsedStates={collapsedStates}
            setCollapsedStates={setCollapsedStates}
            containerRef={ref}
            onBlur={props.onBlur}
          />
          <UpdatesContainer
            collapsedStates={collapsedStates}
            setCollapsedStates={setCollapsedStates}
            numUnreadNotis={numUnreadNotis}
            setNumUnreadMessages={setNumUnreadMessages}
            numUnreadMessages={numUnreadMessages}
            containerRef={ref}
            onBlur={props.onBlur}
          />
          <SettingsContainer
            collapsedStates={collapsedStates}
            setCollapsedStates={setCollapsedStates}
            containerRef={ref}
            onBlur={props.onBlur}
          />
          <AccessibilityContainer
            collapsedStates={collapsedStates}
            setCollapsedStates={setCollapsedStates}
            containerRef={ref}
            onBlur={props.onBlur}
          />
          <div className={`logout_container`}>
            <button
              className="dropdown_item logout"
              role="link"
              aria-label="Logout"
              onClick={e => {
                props.onBlur();
                App.INSTANCE.CurrentUser.Logout();
              }}
              tabIndex={0}
              style={{ border: 'none', width: '100%' }}
            >
              <div className="icon">
                <i className={FontAwesomeConfigurations.ICON_ARROW_RIGHT_FROM_BRACKET}></i>
              </div>
              <div className="text">{CrowdHub_App.LANG.PROFILE_DROPDOWN_LOGOUT}</div>
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
};

const dropdown = {
  hidden: {
    opacity: 0,
    transition: { duration: 0.15 },
    display: 'none',
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.15 },
    display: 'flex',
  },
};

export default ProfileDropdownModule;
